import { useEffect, useRef, useState } from 'react';

/**
 * `input[data-type="number"]` for the SERVICE card money/effort fields —
 * faithful re-platform of service_order.js `validateNumberField` PLUS the
 * board-wide MainView number suite that targets the same selector
 * (numberFocusValidation / numberKeydownValidation / numberKeyupValidation /
 * numberBlurValidation — main.js; the keypress char filter half already runs
 * via the global useGlobalInputBehaviors document listener).
 *
 * The legacy semantics replicated EXACTLY (§B7 / parity 195):
 *  - change-time `validateNumberField`:
 *      value = Number(raw); integer → Math.round (NUMBER), decimal →
 *      toFixed(2) (STRING);
 *      min is DEAD at change time (`input.data('min') ? … : -Infinity` —
 *      data-min="0" is falsy ⇒ -Infinity), only `> max` adds `.invalid`;
 *      non-empty values get comma-formatted into the input
 *      (formatNumberWithCommaSeparator — NaN passes through as 'NaN');
 *      RETURN: `.invalid` ⇒ FALSE (the invalid-saves-false quirk — the model
 *      keeps `false` and the full-model PUT carries it); empty integer ⇒
 *      FALSE (Number('')=0, falsy); empty decimal ⇒ 0 ('0.00' is truthy);
 *      integer garbage ⇒ FALSE (the un-formatted NaN NUMBER is falsy);
 *      decimal garbage ⇒ NaN (the 'NaN' STRING is truthy → Number('NaN') —
 *      JSON-serializes to null).
 *  - keyup: LIVE `.invalid` toggle with the REAL data-min (0) — so a typed
 *    negative flags invalid live and then SAVES FALSE (the change-time pass
 *    never removes the class, `hasClass` ⇒ false).
 *  - focus: un-comma the display (skip when the value ends with '.').
 *  - keydown: char filtering + the 2-decimal typing guard + ArrowUp/Down
 *    spinner stepping clamped to [data-min, data-max] (the spinner DOES use
 *    the real min); a spinner edit commits on blur (the legacy
 *    `.changed-by-spinner` blur → forced change).
 *  - blur without edit: re-group the display.
 *
 * Rendered display derives from the model value through the same pipeline
 * (the legacy re-render runs validateNumbers() over the fresh DOM): a stored
 * `false` renders as "NaN" — quirk kept.
 */

/** fn.uni_tools.formatNumberWithCommaSeparator — type-preserving on NaN. */
function formatCommaLegacy(v: number | string): number | string {
  if (Number.isNaN(Number(v))) return v;
  const grouped = Number(v)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return grouped.includes(',') ? grouped : String(v);
}

/** Display + invalid derivation for a MODEL value (render-time pass). */
export function renderServiceNumber(
  modelValue: number | boolean | string | null | undefined,
  decimal: boolean,
  max: number
): { text: string; invalid: boolean } {
  const raw = modelValue == null ? '' : String(modelValue);
  if (raw === '') return { text: '', invalid: false };
  const num = Number(raw);
  const v: number | string = decimal ? num.toFixed(2) : Math.round(num);
  return { text: String(formatCommaLegacy(v)), invalid: Number(raw) > max };
}

/** Change-time validateNumberField (returns the display + committed value). */
function commitServiceNumber(
  raw: string,
  decimal: boolean,
  max: number,
  invalidNow: boolean
): { text: string; invalid: boolean; committed: number | false } {
  const num = Number(raw);
  let value: number | string = decimal ? num.toFixed(2) : Math.round(num);
  let invalid = invalidNow;
  let text = raw; // empty input keeps its display untouched
  if (raw !== '') {
    if (Number(raw) > max) invalid = true; // min dead at change time
    value = formatCommaLegacy(value);
    text = String(value);
  }
  const numberValue = value ? Number(String(value).replace(/,/g, '')) : false;
  return { text, invalid, committed: invalid ? false : (numberValue as number | false) };
}

const CONTROL_KEYS = [
  'Tab',
  'Backspace',
  'Delete',
  'End',
  'Home',
  'ArrowLeft',
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
];

const ServiceNumberInput = ({
  name,
  value,
  min,
  max,
  step,
  locked,
  onCommit,
}: {
  name: string;
  value: number | boolean | string | null | undefined;
  min: number;
  max: number;
  step: number;
  locked: boolean;
  onCommit: (name: string, value: number | false) => void;
}) => {
  const decimal = step === 0.01;
  const synced = renderServiceNumber(value, decimal, max);
  const [draft, setDraft] = useState(synced.text);
  const [invalid, setInvalid] = useState(synced.invalid);
  const ref = useRef<HTMLInputElement | null>(null);
  const focusEntryRef = useRef<string | null>(null);
  const spinnerRef = useRef(false);

  useEffect(() => {
    if (document.activeElement !== ref.current) {
      setDraft(synced.text);
      setInvalid(synced.invalid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synced.text, synced.invalid]);

  // numberFocusValidation: un-comma unless the value ends with '.'.
  const onFocus = () => {
    let next = draft;
    if (draft.indexOf('.') !== draft.length - 1 && draft) {
      next = String(Number(draft.replace(/,/g, '')));
      setDraft(next);
    }
    focusEntryRef.current = next;
    spinnerRef.current = false;
  };

  // numberKeydownValidation: char filter + 2dp guard + arrow spinner.
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const key = e.key;
    const val = draft;
    const dotIdx = val.indexOf('.');
    const selStart = input.selectionStart ?? 0;
    const isAllSelected = input.selectionStart === 0 && input.selectionEnd === val.length;

    if (!decimal && key === '.') {
      e.preventDefault();
      return;
    }
    if (isAllSelected && key === '.') {
      if (!decimal) e.preventDefault();
      return;
    }
    if (isAllSelected && key === '-') {
      if (!(min < 0)) e.preventDefault();
      return;
    }
    const allowed =
      (key >= '0' && key <= '9' && key.length === 1) ||
      (key === '-' && selStart === 0 && !val.includes('-')) ||
      key === '.' ||
      CONTROL_KEYS.includes(key);
    if (!allowed) {
      e.preventDefault();
      return;
    }
    // extra '.' guard (decimal-with-dot / integer)
    if (((decimal && dotIdx !== -1) || !decimal) && key === '.') {
      e.preventDefault();
      return;
    }
    // 2-decimals typing guard
    if (
      decimal &&
      dotIdx !== -1 &&
      val.length - dotIdx === 3 &&
      selStart > dotIdx &&
      !CONTROL_KEYS.includes(key) &&
      key !== '.'
    ) {
      e.preventDefault();
      return;
    }
    // ArrowUp/ArrowDown spinner (uses the REAL min)
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      const s = key === 'ArrowDown' ? -step : step;
      let nv: number | undefined = val ? Number(val.replace(/,/g, '')) : undefined;
      if (nv === undefined || (!nv && nv !== 0) || Number.isNaN(nv + s)) {
        nv = Number.isFinite(min) ? min : 0;
      } else if (nv + s > max) {
        nv = max;
      } else if (nv + s < min) {
        nv = Number.isFinite(min) ? min : 0;
      } else {
        nv += s;
      }
      setDraft(decimal ? nv.toFixed(2) : String(nv));
      spinnerRef.current = true;
    }
  };

  // numberKeyupValidation: LIVE .invalid toggle with the REAL min/max.
  const onKeyUp = () => {
    const val = ref.current?.value ?? draft;
    if (val !== '') {
      if (Number(val) < min || Number(val) > max) setInvalid(true);
      else setInvalid(false);
    }
  };

  const onBlur = () => {
    const entry = focusEntryRef.current ?? synced.text;
    if (draft !== entry || spinnerRef.current) {
      // change path: validateNumberField → model.set → full-model save.
      const res = commitServiceNumber(draft, decimal, max, invalid);
      setDraft(res.text);
      setInvalid(res.invalid);
      onCommit(name, res.committed);
    } else if (draft.length) {
      // numberBlurValidation: re-group the unchanged display.
      const nv = Number(draft.replace(/,/g, ''));
      setDraft(String(formatCommaLegacy(decimal ? nv.toFixed(2) : nv)));
    }
    focusEntryRef.current = null;
    spinnerRef.current = false;
  };

  return (
    <input
      ref={ref}
      autoComplete="new-password"
      type="text"
      data-type="number"
      name={name}
      className={invalid ? 'invalid' : undefined}
      value={draft}
      data-min={min}
      data-step={step}
      data-max={max}
      readOnly={locked}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={onBlur}
    />
  );
};

export default ServiceNumberInput;
